import { useEffect, useId, useRef, useState } from 'react'
import { Icon, type IconName } from './Icon'
import './Dropdown.css'

export type DropdownOption<T extends string> = {
  value: T
  label: string
  icon?: IconName
}

/**
 * Remplace un `<select>` natif là où son rendu casse le design (panneau
 * `/fitiavana`) : la ligne survolée/sélectionnée d'un `<select>` natif suit
 * le navigateur/l'OS, pas le CSS de l'app — aucune propriété ne la contrôle.
 */
export function Dropdown<T extends string>(
  { value, options, onChange, ariaLabel }: {
    value: T
    options: DropdownOption<T>[]
    onChange: (next: T) => void
    ariaLabel: string
  },
) {
  const [open, setOpen] = useState(false)
  const racine = useRef<HTMLDivElement>(null)
  const listId = useId()
  const current = options.find((o) => o.value === value)

  useEffect(() => {
    if (!open) return
    function surClicDehors(e: MouseEvent) {
      if (racine.current && !racine.current.contains(e.target as Node)) setOpen(false)
    }
    function surEchap(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', surClicDehors)
    document.addEventListener('keydown', surEchap)
    return () => {
      document.removeEventListener('mousedown', surClicDehors)
      document.removeEventListener('keydown', surEchap)
    }
  }, [open])

  function choisir(next: T) {
    onChange(next)
    setOpen(false)
  }

  return (
    <div className="dropdown" ref={racine}>
      <button
        type="button"
        className="dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        {current?.icon && <Icon name={current.icon} size={16} />}
        <span>{current?.label ?? value}</span>
        <Icon name="chevronDown" size={14} />
      </button>

      {open && (
        <ul className="dropdown-panel" role="listbox" id={listId} aria-label={ariaLabel}>
          {options.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={o.value === value ? 'is-selected' : ''}
                onClick={() => choisir(o.value)}
              >
                {o.icon && <Icon name={o.icon} size={16} />}
                <span>{o.label}</span>
                {o.value === value && <Icon name="check" size={16} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
